import Joi from 'joi';
import { BRAND_IDS, resolveBrandId, type BrandId } from '@/brands/brand-ids';

type Environment = {
  appUrl: string;
  loginUrl: string;
  logoutUrl: string;
  logoutCallbackUrl: string;
  tokenEndpoint: string;
  apiUrl: string;
  apiTimeout: number;
  aiWsUrl: string;
  aiAsrProvider?: string;
  aiAsrTier?: string;
  aiAsrManualEndTurn: boolean;
  aiTtsProvider?: string;
  /**
   * Comma-separated list of TTS providers to expose in the pre-session voice picker
   * (e.g. `vieneu,vnpt,google-chirp3`). Lets each environment turn providers on/off
   * without a code change. Empty → fall back to the built-in `enabled` defaults in
   * `TTS_PROVIDERS`.
   */
  aiTtsEnabledProviders: string[];
  coreWsUrl?: string;
  avatarSignalUrl: string;
  avatarProcUrl: string;
  /** Named AI service the avatar processing server should proxy this session to
   *  (`ai_service` query param). Empty → server picks its own AI_SERVICE_DEFAULT. */
  avatarAiService: string;
  /** When true, the avatar processing WebSocket offers a `jwt` subprotocol carrying the
   *  access token. Must stay off until the processing server (lip_sync_ai_system) echoes
   *  the subprotocol back on accept() — otherwise the browser fails the handshake outright
   *  (an unmatched offered subprotocol is a hard WS-spec failure, unrelated to auth being
   *  enabled server-side). */
  avatarAuthEnabled: boolean;
  /** When true, the app runs in a demo/sandbox environment and shows a disclaimer banner. */
  isDemoMode: boolean;
  /** When true, in-app language switching is enabled (the locale toggle is shown and a
   *  stored/browser locale is honored). When false the app is locked to the default language. */
  /** When true, self-service registration is exposed (landing page register CTAs, `/register`
   *  route). When false the route redirects to `/login` — self-signup is off by default. */
  registrationEnabled: boolean;
  /** Master switch for roleplay session recording — off by default, so an environment
   *  records only once it has been set up for it (the mp3 transcode needs ffmpeg on the
   *  Core host). When false nothing is captured and the consent prompt stays hidden:
   *  asking for permission to do something that never happens only confuses users. */
  sessionRecordingEnabled: boolean;
  /** IANA zone session timestamps are rendered in. Must match Core's APP_TIMEZONE:
   *  Core buckets analytics by that zone, so formatting a session date in the
   *  viewer's own zone would put a row under a different day than the chart does. */
  appTimezone: string;
  brand: BrandId;
};

const schema = Joi.object({
  ssoHost: Joi.string().uri().required(),
  ssoClientId: Joi.string().uuid().required(),
  appUrl: Joi.string().uri().required(),
  apiUrl: Joi.string().uri().required(),
  apiTimeout: Joi.number().default(60000),
  aiWsUrl: Joi.string().uri().required(),
  aiAsrProvider: Joi.string().optional(),
  aiAsrTier: Joi.string().optional(),
  aiAsrManualEndTurn: Joi.boolean().default(false),
  aiTtsProvider: Joi.string().optional(),
  aiTtsEnabledProviders: Joi.array().items(Joi.string()).default([]),
  coreWsUrl: Joi.string().uri().optional(),
  avatarSignalUrl: Joi.string().allow('').optional().default(''),
  avatarProcUrl: Joi.string().allow('').optional().default(''),
  avatarAiService: Joi.string().allow('').optional().default(''),
  avatarAuthEnabled: Joi.boolean().default(false),
  demoMode: Joi.boolean().default(false),
  registrationEnabled: Joi.boolean().default(false),
  sessionRecordingEnabled: Joi.boolean().default(false),
  appTimezone: Joi.string()
    .pattern(/^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/)
    .default('Asia/Ho_Chi_Minh'),
  brand: Joi.string()
    .valid(...BRAND_IDS)
    .default('default'),
});

const {
  VITE_SSO_HOST,
  VITE_SSO_CLIENT_ID,
  VITE_APP_URL,
  VITE_API_URL,
  VITE_API_TIMEOUT,
  VITE_AI_WS_URL,
  VITE_AI_ASR_PROVIDER,
  VITE_AI_ASR_TIER,
  VITE_AI_ASR_MANUAL_END_TURN,
  VITE_AI_TTS_PROVIDER,
  VITE_AI_TTS_ENABLED_PROVIDERS,
  VITE_CORE_WS_URL,
  VITE_AVATAR_SIGNAL_URL,
  VITE_AVATAR_PROC_URL,
  VITE_AVATAR_AI_SERVICE,
  VITE_AVATAR_AUTH_ENABLED,
  VITE_DEMO_MODE,
  VITE_REGISTRATION_ENABLED,
  VITE_SESSION_RECORDING_ENABLED,
  VITE_APP_TIMEZONE,
  VITE_BRAND,
} = import.meta.env;

const { value: v, error } = schema.validate(
  {
    ssoHost: VITE_SSO_HOST,
    ssoClientId: VITE_SSO_CLIENT_ID,
    appUrl: VITE_APP_URL,
    apiUrl: VITE_API_URL,
    apiTimeout: VITE_API_TIMEOUT ? parseInt(VITE_API_TIMEOUT, 10) : undefined,
    aiWsUrl: VITE_AI_WS_URL,
    aiAsrProvider: VITE_AI_ASR_PROVIDER,
    aiAsrTier: VITE_AI_ASR_TIER,
    aiAsrManualEndTurn: VITE_AI_ASR_MANUAL_END_TURN === 'true',
    aiTtsProvider: VITE_AI_TTS_PROVIDER,
    aiTtsEnabledProviders: VITE_AI_TTS_ENABLED_PROVIDERS
      ? VITE_AI_TTS_ENABLED_PROVIDERS.split(',')
          .map((p: string) => p.trim())
          .filter(Boolean)
      : [],
    coreWsUrl: VITE_CORE_WS_URL,
    avatarSignalUrl: VITE_AVATAR_SIGNAL_URL || '',
    avatarProcUrl: VITE_AVATAR_PROC_URL || '',
    avatarAiService: VITE_AVATAR_AI_SERVICE || '',
    avatarAuthEnabled: VITE_AVATAR_AUTH_ENABLED === 'true',
    demoMode: VITE_DEMO_MODE === 'true',
    registrationEnabled: VITE_REGISTRATION_ENABLED === 'true',
    sessionRecordingEnabled: VITE_SESSION_RECORDING_ENABLED === 'true',
    appTimezone: VITE_APP_TIMEZONE || undefined,
    brand: resolveBrandId(VITE_BRAND),
  },
  { abortEarly: false }
);

if (error) {
  throw new Error(`Invalid environment configuration: ${error.message}`);
}

// Compose SSO URLs from atomic parts
const oauthParams = new URLSearchParams({
  client_id: v.ssoClientId as string,
  response_type: 'code',
  redirect_uri: `${v.appUrl}/login-callback`,
  scope: 'openid profile email offline_access',
});

const logoutCallbackUrl = `${v.appUrl}/logout-callback`;

// Fallback logout URL (used when Core is unreachable and can't hand back its own
// `/oauth2/logout?...` URL) — must carry client_id + post_logout_redirect_uri or
// FusionAuth ends the session but strands the user on its own page instead of
// returning to the app.
const logoutParams = new URLSearchParams({
  client_id: v.ssoClientId as string,
  post_logout_redirect_uri: logoutCallbackUrl,
});

export const environment: Environment = {
  appUrl: v.appUrl as string,
  loginUrl: `${v.ssoHost}/oauth2/authorize?${oauthParams}`,
  logoutUrl: `${v.ssoHost}/oauth2/logout?${logoutParams}`,
  logoutCallbackUrl,
  tokenEndpoint: `${v.apiUrl}/v1/auth/token`,
  apiUrl: v.apiUrl as string,
  apiTimeout: v.apiTimeout as number,
  aiWsUrl: v.aiWsUrl as string,
  aiAsrProvider: v.aiAsrProvider as string | undefined,
  aiAsrTier: v.aiAsrTier as string | undefined,
  aiAsrManualEndTurn: v.aiAsrManualEndTurn as boolean,
  aiTtsProvider: v.aiTtsProvider as string | undefined,
  aiTtsEnabledProviders: v.aiTtsEnabledProviders as string[],
  coreWsUrl: v.coreWsUrl as string | undefined,
  avatarSignalUrl: v.avatarSignalUrl as string,
  avatarProcUrl: v.avatarProcUrl as string,
  avatarAiService: v.avatarAiService as string,
  avatarAuthEnabled: v.avatarAuthEnabled as boolean,
  isDemoMode: v.demoMode as boolean,
  registrationEnabled: v.registrationEnabled as boolean,
  sessionRecordingEnabled: v.sessionRecordingEnabled as boolean,
  appTimezone: v.appTimezone as string,
  brand: v.brand as BrandId,
};
